import React from 'react';
import { Box, Grid, Paper, Avatar, Typography, Button, useTheme } from '@mui/material';
import GrowthShrimpLineChart from '../GrowthShrimpLineChart';
import { useSpeciesName } from 'modules/pond/api/selector';
import { useTranslation } from 'react-i18next';
import { SpeciesName } from 'modules/pond/constants';

type Props = {
  pdg?: String;
  adg?: String;
  shrimpSizeText?: string;
  hideButton?: boolean;
  showRecordBtn?: boolean;
  refetch?: () => void;
};

type StatSmallCardProps = {
  icon?: React.ReactNode;
  iconSrc?: string;
  label: string;
  value: React.ReactNode;
};

const StatSmallCard: React.FC<StatSmallCardProps> = ({ icon, iconSrc, label, value }) => {
    const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1.5px solid',
        borderColor: theme.palette.primary.main,
        borderRadius: 3,
        p: 4,
        minHeight: 88,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: theme.palette.primary.main,
          width: 48,
          height: 48,
          mr: 2,
          boxShadow: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {iconSrc ? (
          <Box
            component="img"
            src={iconSrc}
            alt={label}
            sx={{ width: 28, height: 28, objectFit: 'contain' }}
          />
        ) : (
          icon ?? null
        )}
      </Avatar>

      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

export default function GrowthCardContent(props: Props) {
  const {
    pdg,
    adg,
    shrimpSizeText: shrimpSizeTextProp,
    hideButton = false,
    showRecordBtn = true,
    refetch,
  } = props;

  const speciesName = useSpeciesName();
  const {t} = useTranslation('growth');
  const shrimpSizeText = shrimpSizeTextProp ?? `60 ${t('one.per.kg')}`;

  return (
    <>
      
      <Box mb={2} mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <StatSmallCard
              iconSrc="/static/icons/growth/graph-up.svg" 
              label={'PDG'}
              value={pdg}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <StatSmallCard
              iconSrc="/static/icons/growth/BarChartOutlined.svg" 
              label={'ADG'}
              value={adg}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <StatSmallCard
              iconSrc={ speciesName === SpeciesName.Shrimp ? "/static/icons/growth/shrimp.svg" : "/static/icons/growth/fish.svg" }
              label={t('size.species',{ speciesName })}
              value={shrimpSizeText}
            />
          </Grid>
        </Grid>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: '1.5px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: 2,
          minHeight: 220,
        }}
      >
        <Typography variant="h5">{t('growth.graph')}</Typography>
        <GrowthShrimpLineChart />
      </Paper>

    </>
  );
}
